import { log } from '@/log'
import { SessionApiService } from '@/services/api/session'
import { SessionApiErrorResponse } from '@/services/api/session/response'
import { InitializeSession } from '@/services/session/initialize-session'
import {
  DataFulfilledSessionCookiePayload,
  isDataFulfilledSessionCookiePayload,
  SessionCookie,
} from '@/services/session/session-cookie'

const namespace = 'load-session-data'

/**
 * セッションデータ付きcookieの存在を保証する
 * - JWTにデータがあれば副作用なしで返す
 * - JWTにデータがなければ、セッション存在保証の後にloadDataを実行する
 */
export class LoadSessionData {
  static callWithDataFulfilledSession<T>(
    callback: (payload: DataFulfilledSessionCookiePayload) => Promise<T>
  ): Promise<T> {
    return LoadSessionData.getInstance().load().then(callback)
  }

  static getInstance(): LoadSessionData {
    if (!LoadSessionData.instance) {
      LoadSessionData.instance = new LoadSessionData()
    }

    return LoadSessionData.instance
  }

  private static instance: LoadSessionData

  // 重複実行を制御するためのキャッシュ
  private loadPromise: Promise<DataFulfilledSessionCookiePayload> | null = null

  private constructor(
    private readonly sessionApiService: SessionApiService = new SessionApiService(),
    private readonly sessionCookie: SessionCookie = new SessionCookie(),
    private readonly initializeSessionExists: InitializeSession = InitializeSession.getInstance()
  ) {}

  load(): Promise<DataFulfilledSessionCookiePayload> {
    if (this.loadPromise) {
      return this.loadPromise
    }

    this.loadPromise = this.innerLoad()
      .then((payload) => {
        log.debug({ service: `${namespace}/load`, payload })

        return payload
      })
      .finally(() => {
        // 次の実行時にはキャッシュを利用しないようにする
        // API側がエラーでセッションデータを破棄した際に再読み込みを行う意図
        // 通常は次回実行時にはcookieから読めるはずなのでパフォーマンス的には懸念はない
        this.loadPromise = null
      })

    return this.loadPromise
  }

  /**
   * 1. すでに正しいフォーマットでセッションデータがcookieに存在すればそれを返す
   * 2. なければAPIを呼び出してセッションデータをロードする
   */
  private async innerLoad(): Promise<DataFulfilledSessionCookiePayload> {
    await this.initializeSessionExists.onInitialized(() => Promise.resolve())

    const current = this.readDataFromCookie()
    if (current) {
      return current
    }

    await this.loadData()

    const loaded = this.readDataFromCookie()
    if (loaded) {
      return loaded
    }

    throw new Error('Data not found in session cookie after loadData')
  }

  private readDataFromCookie(): DataFulfilledSessionCookiePayload | null {
    const payload = this.sessionCookie.readPayload()
    if (!payload) {
      return null
    }

    if (!isDataFulfilledSessionCookiePayload(payload)) {
      return null
    }

    return payload
  }

  private async loadData(): Promise<void> {
    const result = await this.sessionApiService.loadData()

    return result.fold(
      (error: SessionApiErrorResponse) => {
        throw new Error(`${error.errorCode}: ${error.message}`)
      },
      () => Promise.resolve()
    )
  }
}
